import { Component } from '@theme/component';

/**
 * A small utility for setting up a disclosure panel using inert to hide/show content.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility.
 *
 * VJ: originalmente usaba una animación de altura vía CSS grid (`grid-template-rows`
 * animando entre `0fr` y `1fr`). Se quitó esa animación porque Safari en iPhone tiene
 * un bug conocido y confirmado (probado en dispositivo real): la opacidad transiciona
 * bien, pero `grid-template-rows` se queda pegado en `0fr` al expandir, dejando el
 * panel invisible aunque `inert` ya esté correctamente en `false`. Se cambió a un
 * mostrar/ocultar simple con `display`, sin animación, que funciona de forma
 * confiable en todos los navegadores.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    content.inert = expanded;
    content.toggleAttribute('data-disclosure-collapsed', expanded);
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
